/**
 * CheckoutPage - Product checkout with pickup method selection
 * Phase C9: Pickup Method Selection
 * Phase C11: Multi-Shop Checkout
 * Phase C11.5: Checkout Flow Standardization
 *
 * Features:
 * - Auth choice at checkout entry (industry standard)
 * - Guest contact info collection for all purchase types
 * - Select pickup method per shop (service appointment or in-shop)
 * - Choose existing future booking or schedule in-shop pickup
 * - Multi-shop support with independent pickup methods
 * - Payment processing
 * - Order confirmation
 */

import React, { useState, useEffect, useRef } from "react";
import AddressAutocompleteInput from "../../components/AddressAutocompleteInput";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../hooks/useCart";
import { getOrCreateSessionId } from "../../utils/authUtils";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import InlineBookingWidget from "../../components/InlineBookingWidget";
import AuthModal from "../../components/AuthModal";
import AddressModal from "../../components/AddressModal";
import VariantLabel from "../../components/VariantLabel";
import PolicyTextWithFeeHighlight from "../../components/PolicyTextWithFeeHighlight";
import {
  NON_REFUNDABLE_PROCESSING_FEE_NOTICE,
  normalizeStorefrontPolicyText,
} from "../../utils/transactionPolicyText";
import {
  BOOKING_HOLD_EXPIRED_MESSAGE,
  clearCartBookingHold,
  findExpiredCartBookings,
  pruneExpiredCartBookingHolds,
  saveCartBookingHold,
} from "../../utils/bookingReservationHold";
import "./CheckoutPage.css";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, itemCount, totalPrice, clearCart, refreshCart } = useCart();
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);

  // Phase C11.5: Auth and customer info state
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // Billing address state
  const [billingAddress, setBillingAddress] = useState({
    street: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const [showGuestForm, setShowGuestForm] = useState(false);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);

  // State - Per-shop pickup methods (Phase C11)
  const [pickupMethods, setPickupMethods] = useState({}); // { shopId: 'service_appointment' | 'in_shop' }
  const [selectedAppointments, setSelectedAppointments] = useState({}); // { shopId: bookingId }
  const [pickupDates, setPickupDates] = useState({}); // { shopId: 'YYYY-MM-DD' }
  const [pickupTimeWindows, setPickupTimeWindows] = useState({}); // { shopId: 'morning' | 'afternoon' | 'evening' }
  const [pickupNotes, setPickupNotes] = useState({}); // { shopId: 'notes...' }
  const [futureBookingsByShop, setFutureBookingsByShop] = useState({}); // { shopId: [bookings] }
  const [reservedBookingsByShop, setReservedBookingsByShop] = useState({}); // { shopId: [reserved bookings] }
  const [reservedBookingsTotal, setReservedBookingsTotal] = useState(0); // Total price of reserved bookings
  const [shopHours, setShopHours] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [checkoutPoliciesByShop, setCheckoutPoliciesByShop] = useState({});
  const [policyLoading, setPolicyLoading] = useState(false);
  const [acceptedStorePolicies, setAcceptedStorePolicies] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1);

  // Tax preview state
  const [taxPreview, setTaxPreview] = useState({
    subtotal: 0,
    taxAmount: 0,
    total: 0,
    taxRate: 0,
    calculatedAtCheckout: true,
  });
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxPreviewError, setTaxPreviewError] = useState("");
  const taxPreviewRequestIdRef = useRef(0);
  const NON_TAXABLE_TAX_CODE = "txcd_00000000";

  const cartTaxSignature = JSON.stringify(
    cartItems.map((item) => ({
      itemId: item._id,
      productId: item.productId?._id || item.productId,
      quantity: item.quantity || 1,
      price: item.priceAtAdd || 0,
      stripeTaxCode: item.productId?.stripeTaxCode || "",
    })),
  );

  const bookingTaxSignature = JSON.stringify(
    Object.entries(selectedAppointments || {}).map(([shopId, appointment]) => ({
      shopId,
      pickupMethod: pickupMethods[shopId] || "",
      bookingChargeType: appointment?.bookingChargeType || "",
      bookingId: appointment?.bookingId || "",
      amount:
        appointment?.amount ||
        appointment?.totalAmount ||
        appointment?.servicePrice ||
        appointment?.servicePriceAtBooking ||
        appointment?.serviceId?.price ||
        0,
      stripeTaxCode: appointment?.stripeTaxCode || "",
    })),
  );

  // Inline booking state (Phase C11)
  const [showInlineBooking, setShowInlineBooking] = useState({}); // { shopId: boolean }
  const [inlineBookingComplete, setInlineBookingComplete] = useState({}); // { shopId: bookingData }

  const getCartItemProductName = (item) => {
    const product = item.productId || {};
    return product.name || item.name || "Product";
  };

  const getCartItemUnitPrice = (item) => {
    const snapshotPrice = Number(item.priceAtAdd);
    if (Number.isFinite(snapshotPrice) && snapshotPrice > 0) {
      return snapshotPrice;
    }

    const productPrice = Number(item.productId?.price);
    return Number.isFinite(productPrice) && productPrice > 0 ? productPrice : 0;
  };

  // Get shops from cart items
  const shops = cartItems.reduce((acc, item) => {
    const shopId = item.shopId?._id || item.shopId;
    // Base shop name from businessId or shop name
    const baseShopName =
      item.shopId?.businessId?.name || item.shopId?.name || "Unknown Shop";
    // For freelancers, show brand/business name instead of address
    const isFreelancer =
      item.shopId?.isFreelancer || item.shopId?.businessType === "freelancer";
    const shopName = isFreelancer
      ? item.shopId?.businessName || item.shopId?.brandName || baseShopName
      : baseShopName;
    const shopData = item.shopId; // Full shop object for navigation
    if (!acc[shopId]) {
      acc[shopId] = {
        shopId,
        shopName,
        shopData,
        items: [],
      };
    }
    acc[shopId].items.push(item);
    return acc;
  }, {});

  const shopArray = Object.values(shops);
  const stepLabels = isAuthenticated
    ? ["Customer Details", "Pickup Method", "Review & Pay"]
    : ["Contact Info", "Billing Address", "Pickup Method", "Review & Pay"];
  const totalSteps = stepLabels.length;
  const pickupStepNumber = isAuthenticated ? 2 : 3;
  const finalStepNumber = isAuthenticated ? 3 : 4;
  const shopPolicySignature = shopArray
    .map((shop) => String(shop.shopId || ""))
    .sort()
    .join("|");

  // Phase C11.5: Check authentication status on page load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("ogestyle_token");

      if (token) {
        // User is authenticated - fetch their profile
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (response.ok) {
            const user = await response.json();
            setIsAuthenticated(true);
            setCustomerInfo({
              firstName: user.firstName || user.name?.split(" ")[0] || "",
              lastName: user.lastName || user.name?.split(" ")[1] || "",
              email: user.email || "",
              phone: user.phone || "",
            });

            // CRITICAL FIX: Populate billing address from user profile
            if (user.billingAddress) {
              setBillingAddress({
                street: user.billingAddress.street || "",
                city: user.billingAddress.city || "",
                state: user.billingAddress.state || "",
                zipCode:
                  user.billingAddress.zip || user.billingAddress.zipCode || "",
              });
            }

            setAuthModalVisible(false);
            setShowGuestForm(false);
          } else {
            // Token invalid, show auth modal
            localStorage.removeItem("ogestyle_token");
            setAuthModalVisible(true);
          }
        } catch (error) {
          setAuthModalVisible(true);
        }
      } else {
        // No token, show auth modal
        setAuthModalVisible(true);
      }

      setAuthCheckComplete(true);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const fetchShopPolicies = async () => {
      if (!shopArray.length) {
        setCheckoutPoliciesByShop({});
        setPolicyLoading(false);
        return;
      }

      setPolicyLoading(true);
      const policyMap = {};

      await Promise.all(
        shopArray.map(async (shop) => {
          const shopId = String(shop.shopId || "");
          if (!shopId) return;

          try {
            const response = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/shop/${shopId}?limit=1`,
            );

            if (!response.ok) {
              return;
            }

            const data = await response.json();
            const firstProduct = Array.isArray(data?.products)
              ? data.products[0]
              : null;
            const shopPolicySource =
              firstProduct?.shopId || data?.shop || data || {};

            policyMap[shopId] = {
              terms: String(shopPolicySource?.displayPolicyTerms || "").trim(),
              refund: normalizeStorefrontPolicyText(
                String(shopPolicySource?.displayPolicyRefund || "").trim(),
              ),
              cancellation: normalizeStorefrontPolicyText(
                String(shopPolicySource?.displayPolicyCancellation || "").trim(),
              ),
            };
          } catch (fetchError) {
            // Silent fallback: server enforces final policy validation at checkout.
          }
        }),
      );

      setCheckoutPoliciesByShop(policyMap);
      setPolicyLoading(false);
    };

    setAcceptedStorePolicies(false);
    fetchShopPolicies();
  }, [shopPolicySignature]);

  useEffect(() => {
    let isCancelled = false;

    const hasHoursEntries = (value) =>
      Boolean(
        value &&
          typeof value === "object" &&
          !Array.isArray(value) &&
          Object.keys(value).length > 0,
      );

    const fetchShopHours = async () => {
      if (!shopArray.length) {
        if (!isCancelled) setShopHours({});
        return;
      }

      const resolvedHours = {};

      for (const shop of shopArray) {
        const shopId = String(shop?.shopId || "");
        if (!shopId) continue;

        const preferredProfessionalIds = Array.from(
          new Set(
            (shop?.items || [])
              .map((item) => {
                const professional = item?.productId?.professionalId;
                if (!professional) return null;
                const role = String(professional?.role || "");
                if (role !== "R4" && role !== "R7") return null;
                return typeof professional === "string"
                  ? professional
                  : professional?._id;
              })
              .filter(Boolean)
              .map((id) => String(id)),
          ),
        );

        const locationSchedule = shop?.shopData?.schedule;
        const locationBusinessHours = shop?.shopData?.businessHours;

        if (hasHoursEntries(locationSchedule)) {
          resolvedHours[shopId] = locationSchedule;
          continue;
        }

        if (hasHoursEntries(locationBusinessHours)) {
          resolvedHours[shopId] = locationBusinessHours;
          continue;
        }

        const isFreelancerLocation =
          Boolean(shop?.shopData?.isFreelancer) ||
          Boolean(shop?.shopData?.isVirtualLocation) ||
          String(shop?.shopData?.businessType || "").toLowerCase() ===
            "freelancer" ||
          preferredProfessionalIds.length > 0;

        if (!isFreelancerLocation) {
          continue;
        }

        try {
          for (const professionalId of preferredProfessionalIds) {
            const professionalScheduleRes = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/staff/${professionalId}/schedule/public`,
            );
            if (!professionalScheduleRes.ok) continue;

            const professionalScheduleData = await professionalScheduleRes.json();
            if (
              professionalScheduleData?.success &&
              hasHoursEntries(professionalScheduleData.schedule)
            ) {
              resolvedHours[shopId] = professionalScheduleData.schedule;
              break;
            }
          }

          if (hasHoursEntries(resolvedHours[shopId])) {
            continue;
          }

          const ownerStaffId =
            typeof shop?.shopData?.ownerId === "string"
              ? shop.shopData.ownerId
              : shop?.shopData?.ownerId?._id;

          if (ownerStaffId) {
            const ownerScheduleRes = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/staff/${ownerStaffId}/schedule/public`,
            );
            if (ownerScheduleRes.ok) {
              const ownerScheduleData = await ownerScheduleRes.json();
              if (
                ownerScheduleData?.success &&
                hasHoursEntries(ownerScheduleData.schedule)
              ) {
                resolvedHours[shopId] = ownerScheduleData.schedule;
                continue;
              }
            }
          }

          const staffRes = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/staff/by-shop/${shopId}`,
          );
          if (!staffRes.ok) continue;

          const staffData = await staffRes.json();
          const primaryStaff = Array.isArray(staffData?.staff)
            ? staffData.staff[0]
            : null;
          const staffId = primaryStaff?._id;
          if (!staffId) continue;

          const scheduleRes = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/staff/${staffId}/schedule/public`,
          );
          if (!scheduleRes.ok) continue;

          const scheduleData = await scheduleRes.json();
          if (scheduleData?.success && hasHoursEntries(scheduleData.schedule)) {
            resolvedHours[shopId] = scheduleData.schedule;
          }
        } catch (_err) {
        }
      }

      if (!isCancelled) {
        setShopHours(resolvedHours);
      }
    };

    fetchShopHours();

    return () => {
      isCancelled = true;
    };
  }, [shopPolicySignature]);

  useEffect(() => {
    setCheckoutStep(1);
    setError("");
  }, [isAuthenticated]);

  // Fetch tax preview whenever cart changes
  useEffect(() => {
    const fetchTaxPreview = async () => {
      const currentRequestId = ++taxPreviewRequestIdRef.current;
      if (cartItems.length === 0) {
        setTaxPreview({
          subtotal: 0,
          taxAmount: 0,
          total: 0,
          taxRate: 0,
          calculatedAtCheckout: true,
        });
        setTaxLoading(false);
        setTaxPreviewError("");
        return;
      }

      // Check if billing address is complete
      const hasCompleteBillingAddress =
        billingAddress.street &&
        billingAddress.city &&
        billingAddress.state &&
        billingAddress.zipCode;
      // If billing address incomplete, show placeholder
      if (!hasCompleteBillingAddress) {
        setTaxPreview({
          subtotal: totalPrice,
          taxAmount: 0,
          total: totalPrice,
          taxRate: 0,
          calculatedAtCheckout: true,
        });
        setTaxLoading(false);
        setTaxPreviewError("");
        return;
      }

      setTaxLoading(true);
      setTaxPreviewError("");

      try {
        // Build cart items for tax calculation
        const items = cartItems.map((item) => {
          return {
            productId: item.productId?._id || item.productId,
            quantity: item.quantity || 1,
            price: item.priceAtAdd || 0, // Use price stored when added to cart
            stripeTaxCode: item.productId?.stripeTaxCode,
          };
        });

        Object.entries(selectedAppointments || {}).forEach(
          ([shopId, appointment]) => {
            if (pickupMethods[shopId] !== "service_appointment") return;
            if (!appointment || typeof appointment !== "object") return;
            if (appointment.bookingChargeType !== "fresh_checkout") return;

            const bookingAmount = Number(
              appointment.amount ||
                appointment.totalAmount ||
                appointment.servicePrice ||
                appointment.servicePriceAtBooking ||
                appointment.serviceId?.price ||
                0,
            );

            if (!Number.isFinite(bookingAmount) || bookingAmount <= 0) return;

            items.push({
              productId:
                appointment.bookingId ||
                `booking-${shopId}-${appointment.appointmentDate || "date"}-${appointment.startTime || "time"}`,
              quantity: 1,
              price: bookingAmount,
              stripeTaxCode:
                appointment.stripeTaxCode || NON_TAXABLE_TAX_CODE,
            });
          },
        );
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/tax/estimate`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items,
              billingAddress: {
                street: billingAddress.street,
                city: billingAddress.city,
                state: billingAddress.state,
                zipCode: billingAddress.zipCode,
                country: "US",
              },
            }),
          },
        );

        if (currentRequestId !== taxPreviewRequestIdRef.current) {
          return;
        }
        if (response.ok) {
          const data = await response.json();

          if (currentRequestId !== taxPreviewRequestIdRef.current) {
            return;
          }
          setTaxPreview({
            subtotal: data.subtotal,
            taxAmount: data.taxAmount,
            total: data.total,
            taxRate: data.taxRate,
            calculatedAtCheckout: false,
          });
        } else {
          const errorText = await response.text();
          setTaxPreviewError("Unable to estimate tax before checkout.");
        }
      } catch (error) {
        if (currentRequestId !== taxPreviewRequestIdRef.current) {
          return;
        }
        setTaxPreviewError("Unable to estimate tax before checkout.");
      } finally {
        if (currentRequestId === taxPreviewRequestIdRef.current) {
          setTaxLoading(false);
        }
      }
    };

    fetchTaxPreview();
  }, [
    cartTaxSignature,
    bookingTaxSignature,
    totalPrice,
    billingAddress.street,
    billingAddress.city,
    billingAddress.state,
    billingAddress.zipCode,
  ]);

  // Handle auth modal choice
  const handleAuthSuccess = () => {
    // User signed in successfully
    setAuthModalVisible(false);
    // Re-check auth to fetch user profile
    window.location.reload(); // Reload to fetch authenticated state
  };

  const handleGuestCheckout = () => {
    // User chose guest checkout
    setAuthModalVisible(false);
    setShowGuestForm(true);
    setIsAuthenticated(false);
  };

  const handleSignOut = () => {
    localStorage.removeItem("ogestyle_token");
    setIsAuthenticated(false);
    setCustomerInfo({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    });
    setAuthModalVisible(true);
    setShowGuestForm(false);
  };

  const getCurrentSessionId = () => getOrCreateSessionId();

  const releaseReservationById = async (bookingId) => {
    const normalizedBookingId = String(bookingId || "").trim();
    if (!normalizedBookingId || normalizedBookingId.startsWith("inline-")) {
      return;
    }

    const token = localStorage.getItem("ogestyle_token");
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookings/release-reservation`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        bookingId: normalizedBookingId,
        sessionId: getCurrentSessionId(),
      }),
    });
  };

  const clearExpiredCheckoutBookings = async (expiredBookings) => {
    if (!expiredBookings.length) return;

    const expiredIds = new Set(
      expiredBookings
        .map((booking) => String(booking?.bookingId || booking?._id || booking?.bookingData?._id || ""))
        .filter(Boolean),
    );

    expiredIds.forEach((bookingId) => clearCartBookingHold(bookingId));

    setSelectedAppointments((prev) => {
      const next = { ...prev };
      Object.entries(next).forEach(([shopId, appointment]) => {
        const bookingId = String(appointment?.bookingId || appointment?._id || appointment?.bookingData?._id || "");
        if (expiredIds.has(bookingId)) {
          delete next[shopId];
        }
      });
      return next;
    });

    setInlineBookingComplete((prev) => {
      const next = { ...prev };
      Object.entries(next).forEach(([shopId, booking]) => {
        const bookingId = String(booking?.bookingId || booking?._id || booking?.bookingData?._id || "");
        if (expiredIds.has(bookingId)) {
          delete next[shopId];
        }
      });
      return next;
    });

    for (const bookingId of expiredIds) {
      try {
        await releaseReservationById(bookingId);
      } catch (_err) {
      }
    }

    setError(BOOKING_HOLD_EXPIRED_MESSAGE);
  };

  useEffect(() => {
    const checkExpiredReservations = () => {
      pruneExpiredCartBookingHolds();
      const freshBookings = [
        ...Object.values(selectedAppointments || {}),
        ...Object.values(inlineBookingComplete || {}),
      ].filter((booking) => booking?.bookingChargeType === "fresh_checkout");
      const expiredBookings = findExpiredCartBookings(freshBookings);
      if (expiredBookings.length > 0) {
        clearExpiredCheckoutBookings(expiredBookings);
      }
    };

    checkExpiredReservations();
    const intervalId = window.setInterval(checkExpiredReservations, 15000);
    return () => window.clearInterval(intervalId);
  }, [selectedAppointments, inlineBookingComplete]);

  const handlePickupMethodChange = async (shopId, nextMethod) => {
    const previousMethod = pickupMethods[shopId] || "";

    setPickupMethods((prev) => ({
      ...prev,
      [shopId]: nextMethod,
    }));

    if (!(previousMethod === "service_appointment" && nextMethod === "in_shop")) {
      return;
    }

    const appointment = selectedAppointments[shopId];
    const inlineBooking = inlineBookingComplete[shopId];
    const bookingIdsToRelease = new Set();

    if (
      appointment &&
      typeof appointment === "object" &&
      appointment.bookingChargeType === "fresh_checkout"
    ) {
      if (appointment.bookingId) bookingIdsToRelease.add(String(appointment.bookingId));
      if (appointment.bookingData?._id) {
        bookingIdsToRelease.add(String(appointment.bookingData._id));
      }
    }

    if (
      inlineBooking &&
      inlineBooking.bookingChargeType === "fresh_checkout"
    ) {
      if (inlineBooking.bookingId) bookingIdsToRelease.add(String(inlineBooking.bookingId));
      if (inlineBooking._id) bookingIdsToRelease.add(String(inlineBooking._id));
      if (inlineBooking.bookingData?._id) {
        bookingIdsToRelease.add(String(inlineBooking.bookingData._id));
      }
    }

    for (const bookingId of bookingIdsToRelease) {
      try {
        await releaseReservationById(bookingId);
      } catch (_err) {
      }
    }

    setSelectedAppointments((prev) => {
      const next = { ...prev };
      delete next[shopId];
      return next;
    });

    setInlineBookingComplete((prev) => {
      const next = { ...prev };
      delete next[shopId];
      return next;
    });

    setShowInlineBooking((prev) => ({
      ...prev,
      [shopId]: false,
    }));
  };

  // Normalize booking amount across different booking payload shapes
  const getBookingAmount = (booking) => {
    if (!booking) return 0;
    if (booking.bookingChargeType === "existing_paid") return 0;
    return (
      booking.amount ||
      booking.totalAmount ||
      booking.servicePrice ||
      booking.servicePriceAtBooking ||
      booking.serviceId?.price ||
      0
    );
  };

  // Calculate total booking cost (includes both reserved bookings and inline bookings)
  const getTotalBookingCost = () => {
    let total = 0;

    // Charge only fresh bookings created in this checkout flow.
    Object.entries(selectedAppointments).forEach(([shopId, appointment]) => {
      if (pickupMethods[shopId] !== "service_appointment") return;
      if (appointment && typeof appointment === "object") {
        if (appointment.bookingChargeType === "fresh_checkout") {
          total += getBookingAmount(appointment);
        }
      }
    });

    return total;
  };

  const getDisplaySubtotal = () => totalPrice + getTotalBookingCost();

  const getDisplayGrandTotal = () => {
    const subtotal = getDisplaySubtotal();
    const taxAmount = Number(taxPreview.taxAmount) || 0;
    return subtotal + taxAmount;
  };

  const validateContactInfo = (requireFullContact) => {
    if (!customerInfo.email) {
      return "Email address is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerInfo.email)) {
      return "Please enter a valid email address";
    }

    if (requireFullContact) {
      if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.phone) {
        return "Please complete all contact information fields";
      }

      if (customerInfo.phone.length < 10) {
        return "Please enter a valid phone number";
      }
    }

    return "";
  };

  const validateBillingAddress = () => {
    if (
      !billingAddress.street ||
      !billingAddress.city ||
      !billingAddress.state ||
      !billingAddress.zipCode
    ) {
      return isAuthenticated
        ? "Please add your billing address to continue"
        : "Please complete your billing address";
    }

    if (!/^\d{5}$/.test(billingAddress.zipCode)) {
      return "Please enter a valid 5-digit ZIP code";
    }

    return "";
  };

  const validatePickupSelections = () => {
    for (const shop of shopArray) {
      if (shopHasProducts(shop)) {
        const shopPickupMethod = pickupMethods[shop.shopId];

        if (!shopPickupMethod) {
          return `Please select a pickup method for ${shop.shopName}`;
        }

        if (
          shopPickupMethod === "service_appointment" &&
          !selectedAppointments[shop.shopId]
        ) {
          return `Please select an appointment for ${shop.shopName}`;
        }

        if (
          shopPickupMethod === "in_shop" &&
          (!pickupDates[shop.shopId] || !pickupTimeWindows[shop.shopId])
        ) {
          return `Please select pickup date and time for ${shop.shopName}`;
        }

        if (shopPickupMethod === "in_shop") {
          const availableDates = getAvailableDatesForShop(shop);
          if (!availableDates.includes(pickupDates[shop.shopId])) {
            return `${shop.shopName} is closed on the selected pickup date. Please choose an available date.`;
          }

          const availableWindows = getAvailableTimeWindowsForShop(
            shop,
            pickupDates[shop.shopId],
          );
          const hasSelectedWindow = availableWindows.some(
            (window) => window.value === pickupTimeWindows[shop.shopId],
          );
          if (!hasSelectedWindow) {
            return `${shop.shopName} is not open for the selected pickup time window.`;
          }
        }
      }
    }

    return "";
  };

  const handleNextStep = () => {
    let validationError = "";

    if (isAuthenticated && checkoutStep === 1) {
      validationError = validateContactInfo(false) || validateBillingAddress();
    } else if (!isAuthenticated && checkoutStep === 1) {
      validationError = validateContactInfo(true);
    } else if (!isAuthenticated && checkoutStep === 2) {
      validationError = validateBillingAddress();
    } else if (checkoutStep === pickupStepNumber) {
      validationError = validatePickupSelections();
    }

    if (validationError) {
      setError(validationError);
      if (isAuthenticated && checkoutStep === 1 && validationError.includes("billing address")) {
        setShowAddressModal(true);
      }
      return;
    }

    setError("");
    setCheckoutStep((prev) => Math.min(prev + 1, totalSteps));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrevStep = () => {
    setError("");
    setCheckoutStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Helper to format date according to user's locale (MM-DD-YYYY for US)
  const formatDate = (dateString) => {
    if (!dateString) return "";
    // Parse date components directly to avoid timezone offset
    // Backend stores dates as "YYYY-MM-DD" strings
    const [year, month, day] = dateString.split("T")[0].split("-");
    return `${month}-${day}-${year}`;
  };

  // Helper to format time from 24-hour to 12-hour AM/PM
  const formatTime = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Check if cart has only packages (no physical products) - per shop
  const shopHasProducts = (shop) =>
    shop.items.some((item) => item.itemType === "product");
  const hasAnyProducts = shopArray.some((shop) => shopHasProducts(shop));

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch suggestions for empty checkout state so customers can continue shopping quickly.
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (itemCount !== 0) return;

      setSuggestedLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/featured?limit=8`,
        );

        if (!response.ok) {
          throw new Error("Failed to load suggested products");
        }

        const data = await response.json();
        setSuggestedProducts(data.products || []);
      } catch (suggestionError) {
        setSuggestedProducts([]);
      } finally {
        setSuggestedLoading(false);
      }
    };

    fetchSuggestions();
  }, [itemCount]);

  // Fetch future bookings for service appointment option (per shop)
  useEffect(() => {
    const fetchFutureBookingsForAllShops = async () => {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) return;

      const confirmedBookingsByShop = {};
      const reservedBookingsByShop = {};
      let reservedTotal = 0;

      for (const shop of shopArray) {
        try {
          // CRITICAL: Only fetch CONFIRMED bookings for pickup options
          // Reserved bookings should NOT be selectable to prevent fraud
          // If booking is paid, webhook immediately confirms it
          const confirmedResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/bookings/my-bookings?shopId=${shop.shopId}&status=confirmed`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (confirmedResponse.ok) {
            const data = await confirmedResponse.json();
            // Only future and today's confirmed bookings (paid appointments)
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Start of today
            const future =
              data.bookings?.filter(
                (b) => new Date(b.appointmentDate) >= today,
              ) || [];
            confirmedBookingsByShop[shop.shopId] = future;
          }
        } catch (err) {
          confirmedBookingsByShop[shop.shopId] = [];
        }
      }

      setFutureBookingsByShop(confirmedBookingsByShop);
      // No need to fetch reserved bookings - only confirmed (paid) bookings are selectable
      setReservedBookingsByShop({});
      setReservedBookingsTotal(0);
    };

    fetchFutureBookingsForAllShops();
  }, [shopArray.length]); // Only re-run if number of shops changes

  // Refresh bookings for a specific shop (used after inline booking)
  const refreshBookingsForShop = async (shopId) => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      // CRITICAL: Only fetch CONFIRMED bookings (paid appointments)
      const confirmedResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/my-bookings?shopId=${shopId}&status=confirmed`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (confirmedResponse.ok) {
        const data = await confirmedResponse.json();
        const future =
          data.bookings?.filter(
            (b) => new Date(b.appointmentDate) > new Date(),
          ) || [];

        setFutureBookingsByShop((prev) => ({
          ...prev,
          [shopId]: future,
        }));
      }
    } catch (err) {
    }
  };

  const inShopTimeWindows = [
    { value: "morning", label: "Morning (9AM-12PM)", start: 9 * 60, end: 12 * 60 },
    { value: "afternoon", label: "Afternoon (12PM-3PM)", start: 12 * 60, end: 15 * 60 },
    { value: "evening", label: "Evening (3PM-6PM)", start: 15 * 60, end: 18 * 60 },
  ];
  const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const getShopTimezone = (shop) =>
    shop?.shopData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";

  const getShopNowParts = (shop) => {
    const timezone = getShopTimezone(shop);
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(new Date());
      const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));
      const hour = Number(partMap.hour === "24" ? "0" : partMap.hour);
      const minute = Number(partMap.minute || 0);
      return {
        ymd: `${partMap.year}-${partMap.month}-${partMap.day}`,
        minutes: hour * 60 + minute,
      };
    } catch (_err) {
      const now = new Date();
      return {
        ymd: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`,
        minutes: now.getHours() * 60 + now.getMinutes(),
      };
    }
  };

  const parseTimeToMinutes = (value) => {
    const match = String(value || "").match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return hours * 60 + minutes;
  };

  const hasHoursEntries = (value) =>
    Boolean(
      value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        Object.keys(value).length > 0,
    );

  const getScheduleForShop = (shop) => {
    const shopId = String(shop?.shopId || "");
    const locationSchedule = shop?.shopData?.schedule;
    const locationBusinessHours = shop?.shopData?.businessHours;
    const fallbackSchedule = shopHours[shopId];
    const isIndependentHoursShop = (shop?.items || []).some((item) => {
      const role = String(item?.productId?.professionalId?.role || "");
      return role === "R4" || role === "R7";
    });

    if (isIndependentHoursShop) {
      if (hasHoursEntries(fallbackSchedule)) return fallbackSchedule;
      if (hasHoursEntries(locationSchedule)) return locationSchedule;
      if (hasHoursEntries(locationBusinessHours)) return locationBusinessHours;
      return null;
    }

    if (hasHoursEntries(locationSchedule)) return locationSchedule;
    if (hasHoursEntries(locationBusinessHours)) return locationBusinessHours;
    if (hasHoursEntries(fallbackSchedule)) return fallbackSchedule;
    return null;
  };

  const getDaySchedule = (schedule, dayName) => {
    if (!schedule || typeof schedule !== "object" || !dayName) return null;

    const lowerDayName = String(dayName).toLowerCase();
    const candidateKeys = [
      dayName,
      lowerDayName,
      lowerDayName.charAt(0).toUpperCase() + lowerDayName.slice(1),
    ];

    let rawDaySchedule = null;
    for (const key of candidateKeys) {
      const entry = schedule?.[key];
      if (entry && typeof entry === "object") {
        rawDaySchedule = entry;
        break;
      }
    }

    if (!rawDaySchedule) return null;

    const open = rawDaySchedule.open ?? rawDaySchedule.start ?? "";
    const close = rawDaySchedule.close ?? rawDaySchedule.end ?? "";
    const hasOpenClose = Boolean(String(open || "").trim()) && Boolean(String(close || "").trim());
    const activeRaw = rawDaySchedule.active ?? rawDaySchedule.isAvailable;
    const active =
      typeof activeRaw === "boolean" ? activeRaw : hasOpenClose;

    return {
      active,
      open: String(open || "").trim(),
      close: String(close || "").trim(),
    };
  };

  const getAvailableDatesForShop = (shop) => {
    const schedule = getScheduleForShop(shop);
    const dates = [];
    const shopToday = getShopNowParts(shop).ymd;
    const today = new Date(`${shopToday}T12:00:00`);

    for (let i = 0; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const dateYmd = `${yyyy}-${mm}-${dd}`;
      const dayName = weekdayNames[date.getDay()];
      const daySchedule = getDaySchedule(schedule, dayName);
      if (!daySchedule?.active) {
        continue;
      }

      const openMinutes = parseTimeToMinutes(daySchedule.open);
      const closeMinutes = parseTimeToMinutes(daySchedule.close);
      if (!Number.isFinite(openMinutes) || !Number.isFinite(closeMinutes) || closeMinutes <= openMinutes) {
        continue;
      }

      if (dateYmd === shopToday && getAvailableTimeWindowsForShop(shop, dateYmd).length === 0) {
        continue;
      }

      dates.push(dateYmd);
    }

    return dates;
  };

  const getAvailableTimeWindowsForShop = (shop, pickupDate) => {
    const schedule = getScheduleForShop(shop);
    if (!schedule || !pickupDate) return [];

    const parsedDate = new Date(`${pickupDate}T12:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return [];

    const dayName = weekdayNames[parsedDate.getDay()];
    const daySchedule = getDaySchedule(schedule, dayName);
    if (!daySchedule?.active) return [];

    const openMinutes = parseTimeToMinutes(daySchedule.open);
    const closeMinutes = parseTimeToMinutes(daySchedule.close);
    if (!Number.isFinite(openMinutes) || !Number.isFinite(closeMinutes) || closeMinutes <= openMinutes) {
      return [];
    }

    const shopNow = getShopNowParts(shop);
    const isToday = pickupDate === shopNow.ymd;

    return inShopTimeWindows.filter((window) => {
      if (window.start < openMinutes || window.end > closeMinutes) return false;
      if (!isToday) return true;
      return closeMinutes > shopNow.minutes && window.end > shopNow.minutes;
    });
  };

  const handleAddressSave = async (address) => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/users/address`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(address),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to save address");
    }

    const data = await response.json();
    // Update local billingAddress state with saved address
    setBillingAddress({
      street: address.street,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
    });
    setShowAddressModal(false);
    setError("");
    return data;
  };


  const handleCheckout = async () => {
    const contactError = validateContactInfo(!isAuthenticated);
    if (contactError) {
      setError(contactError);
      return;
    }

    const billingError = validateBillingAddress();
    if (billingError) {
      if (isAuthenticated) {
        setShowAddressModal(true);
      }
      setError(billingError);
      return;
    }

    const pickupError = validatePickupSelections();
    if (pickupError) {
      setError(pickupError);
      return;
    }

    if (!acceptedStorePolicies) {
      setError("Please accept seller policies before completing checkout");
      return;
    }

    const freshCheckoutBookings = [
      ...Object.values(selectedAppointments || {}),
      ...Object.values(inlineBookingComplete || {}),
    ].filter((booking) => booking?.bookingChargeType === "fresh_checkout");
    const expiredBookings = findExpiredCartBookings(freshCheckoutBookings);
    if (expiredBookings.length > 0) {
      await clearExpiredCheckoutBookings(expiredBookings);
      return;
    }

    setError("");
    // Every cart line must have a product attached. Without it we cannot price or even
    // identify the line, and the code below dereferences it (`shopProduct._id`) - which is
    // what produced "can't access property _id, shopProduct is undefined" and blew up the
    // whole checkout page.
    //
    // Deliberately REFUSES rather than skipping the bad line: dropping a line we cannot
    // price would quietly under-charge the order. Failing here is the safe outcome.
    const linesMissingProduct = shopArray
      .flatMap((shop) => shop.items || [])
      .filter((item) => !item?.productId);

    if (linesMissingProduct.length > 0) {
      setError(
        "One or more items in your cart are no longer available. Please remove them and try again.",
      );
      return;
    }

    setLoading(true);

    try {
      // Phase C12: Prepare order data for Stripe Checkout Session
      // Use the same total calculation as displayed on page
      const orderTotal = getDisplayGrandTotal();

      const shops = shopArray.map((shop) => {
        const shopPickupMethod = pickupMethods[shop.shopId] || "none";
        const items = shop.items.map((item) => {
          const shopProduct = item.productId;
          const productName = getCartItemProductName(item);
          const productPrice = getCartItemUnitPrice(item);
          return {
            productId: shopProduct._id,
            name: productName || "Product",
            quantity: item.quantity || 1,
            priceAtPurchase: productPrice || 0,
            variantKey: item.variantKey || shopProduct.variantKey || "default",
            variantLabel:
              item.variantLabel ||
              shopProduct.variantLabel ||
              shopProduct.variantKey ||
              "Default",
            variantAttributes:
              item.variantAttributes || shopProduct.variantAttributes || [],
            variantSku: item.variantSku || shopProduct.variantSku || "",
            stripeTaxCode: shopProduct.stripeTaxCode,
            pickupMethod: shopPickupMethod,
            bookingId: (() => {
              if (shopPickupMethod !== "service_appointment") return null;
              const appt = selectedAppointments[shop.shopId];
              const rawId = typeof appt === "object" ? appt?.bookingId : appt;
              // Inline fallback IDs (non-ObjectId) must not be sent to the backend
              if (!rawId || String(rawId).startsWith("inline-")) return null;
              return rawId;
            })(),
            pickupDate:
              shopPickupMethod === "in_shop"
                ? pickupDates[shop.shopId]
                : null,
            pickupTimeWindow:
              shopPickupMethod === "in_shop"
                ? pickupTimeWindows[shop.shopId]
                : null,
            pickupNotes:
              shopPickupMethod === "in_shop"
                ? pickupNotes[shop.shopId] || ""
                : "",
          };
        });

        // Build bookings array from both reserved and inline bookings
        const bookings = [];

        if (shopPickupMethod !== "service_appointment") {
          const productsSubtotal = shop.items.reduce((sum, item) => {
            return sum + getCartItemUnitPrice(item) * item.quantity;
          }, 0);

          return {
            shopId: shop.shopId,
            shopName: shop.shopName,
            items,
            bookings,
            subtotal: productsSubtotal,
          };
        }

        // Check for inline booking (from InlineBookingWidget)
        const inlineBooking = inlineBookingComplete[shop.shopId];
        const appointment = selectedAppointments[shop.shopId];
        if (
          inlineBooking &&
          inlineBooking.bookingChargeType === "fresh_checkout" &&
          !appointment
        ) {
          bookings.push({
            bookingData: inlineBooking.bookingData || inlineBooking,
            serviceName: inlineBooking.serviceName || "Service",
            amount: getBookingAmount(inlineBooking),
            stripeTaxCode:
              inlineBooking.stripeTaxCode || NON_TAXABLE_TAX_CODE,
            bookingComponentType: "booking_fresh_checkout",
            bookingId:
              inlineBooking.bookingId ||
              inlineBooking._id ||
              inlineBooking.bookingData?._id ||
              null,
          });
        }

        // Check for selected appointment (from dropdown or inline reservation)
        if (appointment) {
          if (appointment.bookingData) {
            bookings.push({
              bookingData: appointment.bookingData,
              serviceName: appointment.serviceName || "Service",
              amount: getBookingAmount(appointment),
              stripeTaxCode:
                appointment.stripeTaxCode || NON_TAXABLE_TAX_CODE,
              bookingComponentType: "booking_fresh_checkout",
            });
          } else if (appointment.bookingId && !inlineBooking) {
            bookings.push({
              bookingId: appointment.bookingId,
              serviceName: appointment.serviceName || "Service",
              amount: 0,
              bookingComponentType: "booking_existing_paid",
            });
          }
        }

        // Calculate shop subtotal (products) using same pricing logic as order items
        const productsSubtotal = shop.items.reduce((sum, item) => {
          return sum + getCartItemUnitPrice(item) * item.quantity;
        }, 0);

        // Calculate bookings total for this shop
        const bookingsTotal = bookings.reduce(
          (sum, booking) => sum + (booking.amount || 0),
          0,
        );

        return {
          shopId: shop.shopId,
          shopName: shop.shopName,
          items,
          bookings,
          subtotal: productsSubtotal + bookingsTotal,
        };
      });
      // Create Stripe Checkout Session
      const sessionId = !isAuthenticated ? getOrCreateSessionId() : null;

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/orders/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(isAuthenticated && {
              Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
            }),
            ...(!isAuthenticated && sessionId && { "x-session-id": sessionId }),
          },
          body: JSON.stringify({
            customerInfo,
            billingAddress,
            isAuthenticated,
            shops,
            totalAmount: orderTotal,
            acceptedPolicies: {
              accepted: true,
            },
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        const code = data.error;
        let message =
          data.message || code || "Failed to create checkout session";

        // The server prices every order from its own records. These codes mean
        // the cart disagreed with them, so refresh the cart and say why rather
        // than showing a raw failure.
        if (code === "price_changed") {
          const changes = Array.isArray(data.adjustments)
            ? data.adjustments
            : [];
          const detail = changes
            .map(
              (adjustment) =>
                `${adjustment.name}: $${Number(
                  adjustment.previousAmount || 0,
                ).toFixed(2)} → $${Number(adjustment.currentAmount || 0).toFixed(2)}`,
            )
            .join("  •  ");
          message =
            "Some prices changed while your items were in the cart. Your cart has been updated — please review it and try again." +
            (detail ? ` (${detail})` : "");
          await refreshCart();
        } else if (code === "product_unavailable") {
          message =
            data.message ||
            "Some items are no longer available. Your cart has been updated — please review it and try again.";
          await refreshCart();
        } else if (code === "reservation_expired") {
          message =
            "Your appointment time was released. Please choose an available date and time again.";
          setSelectedAppointments({});
          setInlineBookingComplete({});
        }

        throw new Error(message);
      }

      // Redirect to Stripe Checkout
      if (data.sessionUrl) {
        setIsRedirectingToStripe(true);
        // Clear cart optimistically before redirect (order created, payment pending)
        // Success page will also clear as backup
        clearCart();
        window.location.href = data.sessionUrl;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      setError(err.message || "Failed to process checkout");
      setLoading(false);
    }
  };

  const handleSuggestedProductClick = (product) => {
    const shopId = typeof product.shopId === "object" ? product.shopId?._id : product.shopId;
    navigate(`/product/${product._id}${shopId ? `?shopId=${shopId}` : ""}`);
  };

  if (isRedirectingToStripe) {
    return (
      <>
        <CustomerHeader />
        <div className="checkout-page-empty checkout-redirecting-state">
          <h2>Redirecting to secure payment...</h2>
          <p>Please wait while we open Stripe checkout.</p>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (itemCount === 0) {
    return (
      <>
        <CustomerHeader />
        <div className="checkout-page-empty">
          <h2>Your cart is empty</h2>
          <p>Add some products before checking out.</p>
          <button onClick={() => navigate("/products")}>
            Browse Products
          </button>

          <div className="checkout-suggested-section">
            <h3>Suggested Products</h3>
            {suggestedLoading ? (
              <p className="checkout-suggested-loading">Loading suggestions...</p>
            ) : suggestedProducts.length === 0 ? (
              <p className="checkout-suggested-empty">No suggested products right now.</p>
            ) : (
              <div className="checkout-suggested-grid">
                {suggestedProducts.map((product) => (
                  <div
                    key={product._id}
                    className="checkout-suggested-card"
                    onClick={() => handleSuggestedProductClick(product)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSuggestedProductClick(product);
                      }
                    }}
                  >
                    <div className="checkout-suggested-image-wrap">
                      <img
                        src={product.images?.[0] || product.imageUrl || "/default-product.png"}
                        alt={product.name || "Suggested product"}
                        className="checkout-suggested-image"
                      />
                    </div>
                    <div className="checkout-suggested-info">
                      <p className="checkout-suggested-name">{product.name || "Product"}</p>
                      <p className="checkout-suggested-price">${Number(product.price || 0).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  // Helper to render pickup section for a shop
  const renderPickupSection = (shop) => {
    const shopId = shop.shopId;
    const futureBookings = futureBookingsByShop[shopId] || [];
    const reservedBookings = reservedBookingsByShop[shopId] || [];

    // Include inline booking if present (for guest checkout) and not already in reserved
    const inlineBooking = inlineBookingComplete[shopId];
    const isInlineAlreadyInReserved =
      inlineBooking &&
      reservedBookings.some((b) => b._id === (inlineBooking.bookingId || inlineBooking._id));
    const allBookings = [
      ...futureBookings,
      ...reservedBookings,
      // Always ensure inlineBooking has a stable bookingId for dropdown matching
      ...(inlineBooking && !isInlineAlreadyInReserved
        ? [{
            ...inlineBooking,
            bookingId:
              inlineBooking.bookingId ||
              inlineBooking._id ||
              (inlineBooking.bookingData && inlineBooking.bookingData._id) ||
              `inline-${shopId}`,
            bookingChargeType:
              inlineBooking.bookingChargeType || "fresh_checkout",
          }]
        : []),
    ];

    const shopPickupMethod = pickupMethods[shopId] || "";
    const availablePickupDates = getAvailableDatesForShop(shop);
    const availableTimeWindows = getAvailableTimeWindowsForShop(
      shop,
      pickupDates[shopId] || "",
    );

    return (
      <div className="pickup-section" key={`pickup-${shopId}`}>
        <h3>📦 Pickup Method for {shop.shopName}</h3>

        <div className="pickup-options">
          {/* Option 1: Service Appointment */}
          <div
            className={`pickup-option ${shopPickupMethod === "service_appointment" ? "selected" : ""}`}
          >
            <label className="pickup-option-label">
              <input
                type="radio"
                name={`pickupMethod-${shopId}`}
                value="service_appointment"
                checked={shopPickupMethod === "service_appointment"}
                onChange={(e) => handlePickupMethodChange(shopId, e.target.value)}
              />
              <div className="pickup-option-content">
                <div className="pickup-option-title">
                  During a service appointment
                </div>
                <div className="pickup-option-description">
                  Pick up when you come in for a scheduled service
                </div>
              </div>
            </label>

            {shopPickupMethod === "service_appointment" && (
              <div className="pickup-details">
                {allBookings.length > 0 ? (
                  <select
                    value={
                      typeof selectedAppointments[shopId] === "object"
                        ? selectedAppointments[shopId]?.bookingId || ""
                        : selectedAppointments[shopId] || ""
                    }
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      if (!selectedId) {
                        setSelectedAppointments({
                          ...selectedAppointments,
                          [shopId]: "",
                        });
                        return;
                      }

                      const selectedBooking = allBookings.find(
                        (booking) => (booking.bookingId || booking._id) === selectedId,
                      );

                      // If the selected booking is an inline booking just created in this session, preserve 'fresh_checkout'
                      if (
                        selectedBooking &&
                        selectedBooking.bookingChargeType === "fresh_checkout"
                      ) {
                        setSelectedAppointments({
                          ...selectedAppointments,
                          [shopId]: {
                            bookingId: selectedBooking.bookingId || selectedBooking._id,
                            serviceName:
                              selectedBooking.serviceId?.name ||
                              selectedBooking.serviceName ||
                              "Service",
                            amount:
                              selectedBooking.amount ||
                              selectedBooking.servicePrice ||
                              0,
                            stripeTaxCode:
                              selectedBooking.stripeTaxCode ||
                              NON_TAXABLE_TAX_CODE,
                            bookingChargeType: "fresh_checkout",
                            appointmentDate: selectedBooking.appointmentDate,
                            startTime: selectedBooking.startTime,
                            reservationExpiresAt: selectedBooking.reservationExpiresAt,
                          },
                        });
                      } else {
                        setSelectedAppointments({
                          ...selectedAppointments,
                          [shopId]: selectedBooking
                            ? {
                                bookingId: selectedBooking.bookingId || selectedBooking._id,
                                serviceName:
                                  selectedBooking.serviceId?.name ||
                                  selectedBooking.serviceName ||
                                  "Service",
                                amount: 0,
                                stripeTaxCode:
                                  selectedBooking.stripeTaxCode ||
                                  NON_TAXABLE_TAX_CODE,
                                bookingChargeType: "existing_paid",
                                appointmentDate: selectedBooking.appointmentDate,
                                startTime: selectedBooking.startTime,
                              }
                            : selectedId,
                        });
                      }
                    }}
                    className="pickup-select"
                  >
                    <option key="__select-appt-default" value="">Select an appointment...</option>
                    {allBookings.map((booking, index) => (
                      <option
                        key={
                          booking.bookingId || booking._id ||
                          `${shopId}-${booking.appointmentDate || "date"}-${booking.startTime || "time"}-${index}`
                        }
                        value={booking.bookingId || booking._id}
                      >
                        {formatDate(booking.appointmentDate)} at{" "}
                        {formatTime(booking.startTime)}
                        {booking.serviceId?.name &&
                          ` | ${booking.serviceId.name}`}{" "}
                        {booking.status === "reserved"
                          ? "(Reserved for this order)"
                          : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="no-bookings">
                    <p>
                      You don't have any upcoming appointments at this shop.
                    </p>
                    {!showInlineBooking[shopId] &&
                      !inlineBookingComplete[shopId] && (
                        <button
                          className="book-service-btn"
                          type="button"
                          onClick={() =>
                            setShowInlineBooking({
                              ...showInlineBooking,
                              [shopId]: true,
                            })
                          }
                        >
                          📅 Book a Service Now
                        </button>
                      )}
                  </div>
                )}

                {/* Inline Booking Widget (Phase C11 + C11.5) */}
                {showInlineBooking[shopId] &&
                  !inlineBookingComplete[shopId] && (
                    <InlineBookingWidget
                      shopId={shopId}
                      shopName={shop.shopName}
                      bookingProductIds={shop.items
                        .map((item) => item?.productId?._id || item?.productId)
                        .filter(Boolean)}
                      isAuthenticated={isAuthenticated}
                      customerInfo={customerInfo}
                      onBookingComplete={(booking) => {
                        // Use one deterministic ID so dropdown option and selected value always match.
                        const inlineBookingId =
                          booking.bookingData?._id ||
                          booking._id ||
                          booking.bookingId ||
                          `inline-${shopId}-${booking.appointmentDate || "date"}-${booking.startTime || "time"}`;

                        const bookingInfo = {
                          bookingData: booking.bookingData, // Full booking data for Stripe metadata
                          serviceName: booking.serviceName || "Service",
                          amount: booking.servicePrice || 0,
                          stripeTaxCode:
                            booking.stripeTaxCode || NON_TAXABLE_TAX_CODE,
                          bookingChargeType: "fresh_checkout",
                          appointmentDate: booking.appointmentDate,
                          startTime: booking.startTime,
                          reservationExpiresAt: booking.reservationExpiresAt,
                          bookingId: inlineBookingId,
                        };

                        saveCartBookingHold(bookingInfo);

                        setInlineBookingComplete((prev) => ({
                          ...prev,
                          [shopId]: {
                            ...booking,
                            stripeTaxCode:
                              booking.stripeTaxCode || NON_TAXABLE_TAX_CODE,
                            bookingChargeType: "fresh_checkout",
                            bookingId: inlineBookingId,
                          },
                        }));
                        setSelectedAppointments((prev) => ({
                          ...prev,
                          [shopId]: bookingInfo,
                        }));
                        setShowInlineBooking((prev) => ({
                          ...prev,
                          [shopId]: false,
                        }));
                      }}
                      onCancel={() => {
                        setShowInlineBooking({
                          ...showInlineBooking,
                          [shopId]: false,
                        });
                      }}
                    />
                  )}

                {/* Show success message after inline booking */}
                {inlineBookingComplete[shopId] && (
                  <div className="inline-booking-success-message">
                    ✓ Service reserved! Your appointment has been added to this
                    order.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Option 2: In-Shop Pickup */}
          <div
            className={`pickup-option ${shopPickupMethod === "in_shop" ? "selected" : ""}`}
          >
            <label className="pickup-option-label">
              <input
                type="radio"
                name={`pickupMethod-${shopId}`}
                value="in_shop"
                checked={shopPickupMethod === "in_shop"}
                onChange={(e) => handlePickupMethodChange(shopId, e.target.value)}
              />
              <div className="pickup-option-content">
                <div className="pickup-option-title">In-shop pickup</div>
                <div className="pickup-option-description">
                  Come to the shop to pick up (no appointment needed)
                </div>
              </div>
            </label>

            {shopPickupMethod === "in_shop" && (
              <div className="pickup-details">
                <div className="pickup-field">
                  <label>Pickup Date:</label>
                  <select
                    value={pickupDates[shopId] || ""}
                    onChange={(e) => {
                      const nextDate = e.target.value;
                      const nextWindows = getAvailableTimeWindowsForShop(shop, nextDate);
                      const selectedWindow = pickupTimeWindows[shopId] || "";
                      const keepCurrentWindow = nextWindows.some((window) => window.value === selectedWindow);

                      setPickupDates({
                        ...pickupDates,
                        [shopId]: nextDate,
                      });
                      setPickupTimeWindows({
                        ...pickupTimeWindows,
                        [shopId]: keepCurrentWindow ? selectedWindow : "",
                      });
                    }}
                    className="pickup-select"
                  >
                    <option key="__pickup-date-default" value="">Select a date...</option>
                    {availablePickupDates.map((date) => (
                      <option key={date} value={date}>
                        {new Date(`${date}T12:00:00`).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pickup-field">
                  <label>Pickup Time Window:</label>
                  <select
                    value={pickupTimeWindows[shopId] || ""}
                    onChange={(e) =>
                      setPickupTimeWindows({
                        ...pickupTimeWindows,
                        [shopId]: e.target.value,
                      })
                    }
                    className="pickup-select"
                  >
                    <option key="__pickup-time-default" value="">Select a time window...</option>
                    {availableTimeWindows.map((window) => (
                      <option key={window.value} value={window.value}>
                        {window.label}
                      </option>
                    ))}
                  </select>
                  {pickupDates[shopId] && availableTimeWindows.length === 0 && (
                    <div style={{ fontSize: "12px", color: "#92400e", marginTop: "6px" }}>
                      No in-shop windows are available for this date. Please choose another date.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Pickup Notes */}
          {shopPickupMethod && (
            <div className="pickup-notes-field">
              <label>Special Instructions (Optional):</label>
              <textarea
                value={pickupNotes[shopId] || ""}
                onChange={(e) =>
                  setPickupNotes({ ...pickupNotes, [shopId]: e.target.value })
                }
                placeholder="Any special requests or notes for the shop..."
                maxLength={500}
                rows={3}
              />
              <div className="char-count">
                {(pickupNotes[shopId] || "").length}/500
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <CustomerHeader />
      <div className="checkout-page">
        <div className="checkout-container">
          <h1>Checkout</h1>

          {authCheckComplete && !authModalVisible && (
            <div className="checkout-stepper" aria-label="Checkout steps">
              {stepLabels.map((label, index) => {
                const stepNumber = index + 1;
                const isActive = checkoutStep === stepNumber;
                const isCompleted = checkoutStep > stepNumber;
                return (
                  <div
                    key={label}
                    className={`checkout-step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                  >
                    <span className="checkout-step-number">{stepNumber}</span>
                    <span className="checkout-step-label">{label}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Phase C11.5: Auth Choice Modal (shows on page load if not authenticated) */}
          {authModalVisible && !authCheckComplete && (
            <div className="auth-check-loading">
              <p>Loading...</p>
            </div>
          )}

          {authModalVisible && authCheckComplete && (
            <AuthModal
              isOpen={authModalVisible}
              onClose={() => {
                // Don't allow closing without making a choice
              }}
              onSuccess={handleAuthSuccess}
              showGuestOption={true}
              onGuestCheckout={handleGuestCheckout}
            />
          )}

          {/* Phase C11.5: Customer Info Display */}
          {authCheckComplete && !authModalVisible && checkoutStep === 1 && (
            <div className="customer-info-section">
              {isAuthenticated ? (
                <div className="authenticated-checkout">
                  <div className="auth-status">
                    <span className="checkmark">✓</span>
                    <div>
                      <p className="customer-name">
                        <strong>
                          {customerInfo.firstName} {customerInfo.lastName}
                        </strong>
                      </p>
                      <p className="customer-email">{customerInfo.email}</p>
                    </div>
                  </div>
                  <button className="sign-out-btn" onClick={handleSignOut}>
                    Use different email
                  </button>
                </div>
              ) : showGuestForm ? (
                <div className="guest-info-form">
                  <h3>Contact Information</h3>
                  <p className="guest-notice">
                    We'll use this to send your order confirmation and updates.
                  </p>

                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        value={customerInfo.firstName}
                        onChange={(e) =>
                          setCustomerInfo({
                            ...customerInfo,
                            firstName: e.target.value,
                          })
                        }
                        placeholder="John"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        value={customerInfo.lastName}
                        onChange={(e) =>
                          setCustomerInfo({
                            ...customerInfo,
                            lastName: e.target.value,
                          })
                        }
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Email Address *</label>
                      <input
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) =>
                          setCustomerInfo({
                            ...customerInfo,
                            email: e.target.value,
                          })
                        }
                        placeholder="john.doe@example.com"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        value={customerInfo.phone}
                        onChange={(e) =>
                          setCustomerInfo({
                            ...customerInfo,
                            phone: e.target.value,
                          })
                        }
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>
                  </div>

                  <p className="guest-account-prompt">
                    Already have an account?{" "}
                    <button
                      className="link-btn"
                      onClick={() => setAuthModalVisible(true)}
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              ) : null}
            </div>
          )}

          {/* Billing Address Section - Guest Step 2 */}
          {!isAuthenticated && checkoutStep === 2 && (
            <div className="checkout-section">
              <h3>💳 Billing Address</h3>
              <p className="billing-notice">
                This address will be used for payment verification.
              </p>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Street Address *</label>
                  <AddressAutocompleteInput
                    value={billingAddress.street}
                    onChange={(val) =>
                      setBillingAddress({ ...billingAddress, street: val })
                    }
                    onAddressSelect={({ street, city, state, zip }) =>
                      setBillingAddress((prev) => ({
                        ...prev,
                        street: street || prev.street,
                        city: city || prev.city,
                        state: state ? state.toUpperCase() : prev.state,
                        zipCode: zip || prev.zipCode,
                      }))
                    }
                    placeholder="123 Main Street"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={billingAddress.city}
                    onChange={(e) =>
                      setBillingAddress({
                        ...billingAddress,
                        city: e.target.value,
                      })
                    }
                    placeholder="Chicago"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    value={billingAddress.state}
                    onChange={(e) =>
                      setBillingAddress({
                        ...billingAddress,
                        state: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="IL"
                    maxLength="2"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>ZIP Code *</label>
                  <input
                    type="text"
                    value={billingAddress.zipCode}
                    onChange={(e) =>
                      setBillingAddress({
                        ...billingAddress,
                        zipCode: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    placeholder="60601"
                    maxLength="5"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Billing Address Section - Signed-in Step 1 */}
          {isAuthenticated && checkoutStep === 1 && (
            <div className="checkout-section">
              <h3>💳 Billing Address</h3>
              {billingAddress.street &&
              billingAddress.city &&
              billingAddress.state &&
              billingAddress.zipCode ? (
                <p className="billing-notice">
                  {billingAddress.street}, {billingAddress.city}, {billingAddress.state} {billingAddress.zipCode}
                </p>
              ) : (
                <p className="billing-notice">
                  No billing address saved yet. Add your address to continue.
                </p>
              )}
              <button type="button" className="book-service-btn" onClick={() => setShowAddressModal(true)}>
                {billingAddress.street ? "Edit Billing Address" : "Add Billing Address"}
              </button>
            </div>
          )}

          {checkoutStep === pickupStepNumber && shopArray.length > 1 && (
            <div className="multi-shop-notice">
              <p>
                �️ You're ordering from {shopArray.length} different shops in one
                checkout. Select pickup options for each shop below.
              </p>
            </div>
          )}

          {error && <div className="checkout-error">{error}</div>}

          {/* Loop through each shop */}
          {checkoutStep === pickupStepNumber &&
            shopArray.map((shop, index) => {
            const shopTotal = shop.items.reduce(
              (sum, item) => sum + item.priceAtAdd * (item.quantity || 1),
              0,
            );

            // Only inline bookings (created during this checkout) should appear here
            // We don't fetch reservedBookingsByShop anymore - only confirmed bookings
            const inlineBooking = inlineBookingComplete[shop.shopId];
            const allShopBookings = inlineBooking ? [inlineBooking] : [];

            const shopReservedTotal = allShopBookings.reduce(
              (sum, b) => sum + getBookingAmount(b),
              0,
            );
            const shopGrandTotal = shopTotal + shopReservedTotal;

            return (
              <div key={shop.shopId} className="shop-checkout-section">
                {shopArray.length > 1 && (
                  <div className="shop-header">
                    <h2>🏪 {shop.shopName}</h2>
                  </div>
                )}

                {/* Order Summary for this shop */}
                <div className="checkout-section">
                  <h3>
                    {shopArray.length === 1
                      ? "Order Summary"
                      : "Items from this shop"}
                  </h3>
                  <div className="checkout-items">
                    {shop.items.map((item) => (
                      <div key={item._id} className="checkout-item">
                        <div>
                          <span className="checkout-item-name">
                            {item.itemType === "package"
                              ? item.packageId?.name || "Service Package"
                              : item.productId?.masterProductId?.name ||
                                "Product"}
                            {item.itemType !== "package" && ` × ${item.quantity}`}
                          </span>
                          {/* Phase 4: Display variant label */}
                          {item.itemType === "product" && (
                            <div style={{ marginTop: "4px" }}>
                              <VariantLabel item={item} showIcon={true} />
                            </div>
                          )}
                        </div>
                        <span className="checkout-item-price">
                          ${(item.priceAtAdd * (item.quantity || 1)).toFixed(2)}
                        </span>
                      </div>
                    ))}

                    {/* Inline bookings (created during this checkout session) */}
                    {allShopBookings.map((booking, bookingIndex) => (
                      <div
                        key={
                          booking._id ||
                          booking.bookingData?._id ||
                          `${shop.shopId}-${booking.serviceName || "booking"}-${booking.appointmentDate || "date"}-${booking.startTime || bookingIndex}`
                        }
                        className="checkout-item reserved-booking-item"
                      >
                        <span className="checkout-item-name">
                          <span className="service-with-badge">
                            {booking.serviceName}
                            <span className="reserved-badge-inline">
                              (Pending Payment)
                            </span>
                          </span>
                          <span className="booking-datetime">
                            {booking.appointmentDate} at{" "}
                            {formatTime(booking.startTime)}
                          </span>
                        </span>
                        <span className="checkout-item-price">
                          ${getBookingAmount(booking).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="checkout-subtotal">
                    <span>
                      {shopArray.length === 1 ? "Subtotal:" : "Shop Subtotal:"}
                    </span>
                    <span className="subtotal-amount">
                      ${shopGrandTotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Pickup Method - Only for shops with products */}
                {shopHasProducts(shop) && renderPickupSection(shop)}

                {/* Package-only message for this shop */}
                {!shopHasProducts(shop) && (
                  <div className="checkout-section">
                    <h3>💳 Package Purchase</h3>
                    <p className="package-info">
                      Service packages will be added to your account after
                      payment. Schedule appointments anytime from your
                      dashboard.
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {authCheckComplete && !authModalVisible && (
            <div className="checkout-actions checkout-step-actions">
              {checkoutStep > 1 ? (
                <button className="back-to-cart-btn" onClick={handlePrevStep}>
                  ← Back
                </button>
              ) : (
                <button
                  className="back-to-cart-btn"
                  onClick={() => navigate("/cart")}
                >
                  ← Back to Cart
                </button>
              )}

              {checkoutStep < finalStepNumber && (
                <button className="checkout-btn primary" onClick={handleNextStep}>
                  Continue
                </button>
              )}
            </div>
          )}

          {checkoutStep === finalStepNumber && (
            <>
          {/* Overall Total with Tax Preview */}
          <div className="checkout-section final-total-section">
            <div className="checkout-total-breakdown">
              <div className="total-row subtotal-row">
                <span>Subtotal:</span>
                <span>${getDisplaySubtotal().toFixed(2)}</span>
              </div>
              <div className="total-row tax-row">
                <span>
                  Tax{typeof taxPreview.taxRate === 'number' && taxPreview.taxRate > 0 ? ` (${taxPreview.taxRate.toFixed(2)}%)` : ''}:
                </span>
                <span>
                  {taxLoading
                    ? <span className="tax-loading">Calculating...</span>
                    : taxPreviewError
                      ? taxPreviewError
                      : `$${Number(taxPreview.taxAmount || 0).toFixed(2)}`}
                </span>
              </div>
              <div className="total-row grand-total-row">
                <span>Estimated Total:</span>
                <span className="total-amount">${getDisplayGrandTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="checkout-section checkout-policy-consent">
            <h3>Store Policies</h3>
            <p className="checkout-policy-copy">
              Please review each seller policy and confirm acceptance before
              payment.
            </p>

            {policyLoading ? (
              <p className="checkout-policy-loading">Loading seller policies...</p>
            ) : (
              <div className="checkout-policy-list">
                {shopArray.map((shop) => {
                  const policy = checkoutPoliciesByShop[String(shop.shopId)] || {};
                  const hasPolicyDocs =
                    Boolean(policy.terms) &&
                    Boolean(policy.refund) &&
                    Boolean(policy.cancellation);

                  return (
                    <div className="checkout-policy-card" key={`policy-${shop.shopId}`}>
                      <h4>{shop.shopName}</h4>
                      {hasPolicyDocs ? (
                        <>
                          <details>
                            <summary>Terms</summary>
                            <PolicyTextWithFeeHighlight text={policy.terms} />
                          </details>
                          <details>
                            <summary>Refund</summary>
                            <PolicyTextWithFeeHighlight text={policy.refund} />
                          </details>
                          <details>
                            <summary>Cancellation</summary>
                            <PolicyTextWithFeeHighlight text={policy.cancellation} />
                          </details>
                        </>
                      ) : (
                        <p className="checkout-policy-missing">
                          Policy text is still syncing for this seller. Checkout
                          will validate policy availability before payment.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <label className="checkout-policy-checkbox-row">
              <input
                type="checkbox"
                checked={acceptedStorePolicies}
                onChange={(event) => {
                  setAcceptedStorePolicies(event.target.checked);
                  if (error) {
                    setError("");
                  }
                }}
              />
              <span>
                I have reviewed and accept each seller's terms, refund, and
                cancellation policies.
              </span>
            </label>
          </div>

          <div
            className="checkout-section"
            style={{
              background: "#fff7ed",
              border: "1px solid #fdba74",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: "8px", color: "#9a3412" }}>
              Important Payment Notice
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#7c2d12",
              }}
            >
              {NON_REFUNDABLE_PROCESSING_FEE_NOTICE}
            </p>
          </div>

          {/* Unified Checkout Button */}
          <div className="checkout-actions">
            <button className="back-to-cart-btn" onClick={handlePrevStep}>
              ← Previous Step
            </button>
            <button
              className="checkout-btn primary"
              onClick={handleCheckout}
              disabled={loading || policyLoading || !acceptedStorePolicies}
            >
              {loading
                ? "Processing..."
                : `Complete Order - $${getDisplayGrandTotal().toFixed(2)}`}
            </button>
          </div>

          {/* Back to Cart */}
          <div className="checkout-actions">
            <button
              className="back-to-cart-btn"
              onClick={() => navigate("/cart")}
            >
              ← Back to Cart
            </button>
          </div>
            </>
          )}
        </div>
      </div>

      {/* Phase C11.5: Old inline booking auth modal removed - auth handled at page top */}

      {/* Address Collection Modal */}
      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onAddressSave={handleAddressSave}
        initialAddress={billingAddress}
        isRequired={true}
        title="Add Your Address"
        subtitle="We need your address for tax calculation and order delivery"
      />

      <CustomerFooter />
    </>
  );
};

export default CheckoutPage;
